import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { User, Phone, Mail, Globe, Building2, QrCode, ChevronRight, ChevronLeft, Check, X, Camera } from "lucide-react";
import { trackEvent } from "@/lib/tracking";

const STEPS = [
  { label: "Profile", icon: User },
  { label: "Contact", icon: Phone },
  { label: "QR Code", icon: QrCode },
];

const PRESETS = [
  { id: "full", name: "Full Page", desc: "Show everything" },
  { id: "social", name: "Social", desc: "Links focused" },
  { id: "contact", name: "Contact", desc: "Save contact card" },
  { id: "business", name: "Business", desc: "Professional" },
  { id: "minimal", name: "Minimal", desc: "Just links" },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({ username: "", title: "" });
  const [contactData, setContactData] = useState({ email_public: "", phone: "", website: "", company: "" });
  const [variantPreset, setVariantPreset] = useState("full");
  const [variantName, setVariantName] = useState("My QR");
  const [createdVariant, setCreatedVariant] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Check if profile already exists
  useEffect(() => {
    api.get("/profile").then(res => {
      if (res.data && res.data.username) {
        // Profile exists, check for variants
        api.get("/variants").then(vRes => {
          if (vRes.data && vRes.data.length > 0) {
            navigate("/dashboard", { replace: true });
          }
        });
      }
    }).catch(() => {});
  }, [navigate]);

  const handleSkip = () => navigate("/dashboard", { replace: true });

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    if (!profileData.username.trim()) {
      toast.error("Username is required");
      return false;
    }
    setSaving(true);
    try {
      // Create profile
      await api.post("/profile", {
        username: profileData.username.trim().toLowerCase(),
        title: profileData.title,
        ...contactData,
      });
      // Upload avatar if selected
      if (avatarFile) {
        const fd = new FormData();
        fd.append("file", avatarFile);
        await api.post("/upload/profile_image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save profile");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveContact = async () => {
    setSaving(true);
    try {
      await api.post("/profile", contactData);
      return true;
    } catch (e) {
      toast.error("Failed to save contact info");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const createVariant = async () => {
    setSaving(true);
    try {
      const res = await api.post("/variants", { name: variantName, preset: variantPreset });
      setCreatedVariant(res.data);
      // Get QR preview
      const img = await api.get(`/variants/${res.data.id}/image?format=png&size=8&base_url=${encodeURIComponent(BACKEND_URL)}`);
      setQrPreview(img.data?.data);
      toast.success("QR variant created!");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create variant");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (step === 0) {
      const ok = await saveProfile();
      if (!ok) return;
    } else if (step === 1) {
      const ok = await saveContact();
      if (!ok) return;
    } else if (step === 2) {
      if (!createdVariant) {
        const ok = await createVariant();
        if (!ok) return;
      }
      trackEvent("onboarding_complete");
      navigate("/dashboard", { replace: true });
      return;
    }
    setStep((s) => Math.min(s + 1, 2));
  };

  const shareUrl = createdVariant
    ? `${window.location.origin}/v/${createdVariant.token}`
    : "";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center mx-auto mb-3">
            <QrCode size={20} className="text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold font-heading" data-testid="onboarding-title">Welcome to Smart-QR</h1>
          <p className="text-sm text-muted-foreground mt-1">Let's get you set up in 3 quick steps</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-1 mb-8" data-testid="onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                i < step ? "bg-primary text-primary-foreground" :
                i === step ? "bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-2 ring-offset-background" :
                "bg-secondary text-muted-foreground"
              }`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              {i < 2 && <div className={`w-12 h-0.5 mx-1 ${i < step ? "bg-primary" : "bg-secondary"}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <Card className="border-border/50" data-testid="onboarding-card">
          <CardContent className="pt-6">
            {/* Step 1: Profile */}
            {step === 0 && (
              <div className="space-y-5" data-testid="onboarding-step-profile">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2"><User size={18} className="text-primary" /> Create Your Profile</h2>
                  <p className="text-xs text-muted-foreground mt-1">This is your public page identity</p>
                </div>

                {/* Avatar */}
                <div className="flex items-center gap-4">
                  <label className="relative cursor-pointer group">
                    <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center overflow-hidden border-2 border-border group-hover:border-primary/50 transition-colors">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                      ) : (
                        <Camera size={20} className="text-muted-foreground" />
                      )}
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handleAvatarSelect} data-testid="onboarding-avatar-input" />
                  </label>
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium text-foreground">Profile Photo</p>
                    <p>Optional — you can add or adjust later</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ob-username">Username <span className="text-destructive">*</span></Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground shrink-0">/p/</span>
                    <Input id="ob-username" value={profileData.username} onChange={(e) => setProfileData(p => ({ ...p, username: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "") }))} placeholder="yourname" data-testid="onboarding-username" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-title">Display Name</Label>
                  <Input id="ob-title" value={profileData.title} onChange={(e) => setProfileData(p => ({ ...p, title: e.target.value }))} placeholder="John Smith" data-testid="onboarding-title-input" />
                </div>
              </div>
            )}

            {/* Step 2: Contact */}
            {step === 1 && (
              <div className="space-y-5" data-testid="onboarding-step-contact">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2"><Phone size={18} className="text-primary" /> Contact Info</h2>
                  <p className="text-xs text-muted-foreground mt-1">Visitors can save your contact directly to their phone</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-xs"><Mail size={11} /> Public Email</Label>
                    <Input value={contactData.email_public} onChange={(e) => setContactData(c => ({ ...c, email_public: e.target.value }))} placeholder="hello@example.com" data-testid="onboarding-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-xs"><Phone size={11} /> Phone</Label>
                    <Input value={contactData.phone} onChange={(e) => setContactData(c => ({ ...c, phone: e.target.value }))} placeholder="+1-555-0123" data-testid="onboarding-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-xs"><Globe size={11} /> Website</Label>
                    <Input value={contactData.website} onChange={(e) => setContactData(c => ({ ...c, website: e.target.value }))} placeholder="https://example.com" data-testid="onboarding-website" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-xs"><Building2 size={11} /> Company</Label>
                    <Input value={contactData.company} onChange={(e) => setContactData(c => ({ ...c, company: e.target.value }))} placeholder="Acme Corp" data-testid="onboarding-company" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: QR Variant */}
            {step === 2 && (
              <div className="space-y-5" data-testid="onboarding-step-qr">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2"><QrCode size={18} className="text-primary" /> Your First QR Code</h2>
                  <p className="text-xs text-muted-foreground mt-1">Choose a preset and name for your first QR variant</p>
                </div>

                {!createdVariant ? (
                  <>
                    <div className="space-y-2">
                      <Label>Variant Name</Label>
                      <Input value={variantName} onChange={(e) => setVariantName(e.target.value)} placeholder="My QR" data-testid="onboarding-variant-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Preset Style</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {PRESETS.map((p) => (
                          <button key={p.id} onClick={() => setVariantPreset(p.id)}
                            className={`p-3 rounded-lg border text-left transition-all text-sm ${variantPreset === p.id ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
                            data-testid={`onboarding-preset-${p.id}`}>
                            <p className="font-medium text-xs">{p.name}</p>
                            <p className="text-[10px] text-muted-foreground">{p.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center space-y-4" data-testid="onboarding-qr-result">
                    {qrPreview && (
                      <div className="inline-block rounded-2xl bg-white p-4 shadow-lg">
                        <img src={qrPreview} alt="QR Code" className="w-40 h-40" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold text-green-500 flex items-center justify-center gap-1.5"><Check size={16} /> QR Code Created!</p>
                      <p className="text-xs text-muted-foreground mt-1.5">Share link:</p>
                      <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); }}
                        className="text-xs text-primary hover:underline break-all mt-1" data-testid="onboarding-share-link">
                        {shareUrl}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-4 border-t border-border/50">
              <div>
                {step > 0 ? (
                  <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} data-testid="onboarding-back">
                    <ChevronLeft size={16} className="mr-1" /> Back
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground" data-testid="onboarding-skip">
                    <X size={14} className="mr-1" /> Skip for now
                  </Button>
                )}
              </div>
              <Button onClick={handleNext} disabled={saving} className="btn-glow" data-testid="onboarding-next">
                {saving ? "Saving..." :
                 step === 2 && createdVariant ? "Go to Dashboard" :
                 step === 2 ? "Create QR" :
                 step === 1 ? "Next" :
                 "Save & Continue"}
                {!saving && step < 2 && <ChevronRight size={16} className="ml-1" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Skip hint */}
        {step === 1 && (
          <p className="text-center text-[11px] text-muted-foreground mt-4">
            All fields are optional. You can always add these later from your profile.
          </p>
        )}
      </div>
    </div>
  );
}
